// Lightweight timer wrapper so UI can react to start/stop events.
import type { ElapsedTimeTracker } from '../hooks/use-elapsed-time'

export type SendMessageTimerEvent =
  | {
      type: 'start'
      startedAt: number
      messageId: string
      agentId?: string
    }
  | {
      type: 'stop'
      startedAt: number
      finishedAt: number
      elapsedMs: number
      messageId: string
      agentId?: string
      outcome: 'success' | 'error' | 'aborted'
    }

export type SendMessageTimerOutcome = 'success' | 'error' | 'aborted'

export interface SendMessageTimerController {
  start: (messageId: string) => void
  stop: (outcome: SendMessageTimerOutcome) => {
    finishedAt: number
    elapsedMs: number
  } | null
  pause: () => void
  resume: () => void
  isActive: () => boolean
}

export interface SendMessageTimerControllerOptions {
  mainAgentTimer: ElapsedTimeTracker
  onTimerEvent: (event: SendMessageTimerEvent) => void
  agentId?: string
  now?: () => number
}

export const createSendMessageTimerController = (
  options: SendMessageTimerControllerOptions,
): SendMessageTimerController => {
  const {
    mainAgentTimer,
    onTimerEvent,
    agentId,
    now = () => Date.now(),
  } = options

  let timerStartedAt: number | null = null
  let timerMessageId: string | null = null
  let timerActive = false

  const start = (messageId: string) => {
    if (timerActive) {
      return
    }
    timerActive = true
    timerMessageId = messageId
    timerStartedAt = now()
    mainAgentTimer.start()
    const startEvent: SendMessageTimerEvent = {
      type: 'start',
      startedAt: timerStartedAt,
      messageId,
    }
    if (agentId) {
      startEvent.agentId = agentId
    }
    onTimerEvent(startEvent)
  }

  const stop = (outcome: SendMessageTimerOutcome) => {
    if (!timerActive || timerStartedAt == null || !timerMessageId) {
      return null
    }
    timerActive = false
    mainAgentTimer.stop()
    const finishedAt = now()
    const elapsedMs = Math.max(0, finishedAt - timerStartedAt)
    const stopEvent: SendMessageTimerEvent = {
      type: 'stop',
      startedAt: timerStartedAt,
      finishedAt,
      elapsedMs,
      messageId: timerMessageId,
      outcome,
    }
    if (agentId) {
      stopEvent.agentId = agentId
    }
    onTimerEvent(stopEvent)
    timerStartedAt = null
    timerMessageId = null
    return { finishedAt, elapsedMs }
  }

  const pause = () => {
    mainAgentTimer.pause()
  }

  const resume = () => {
    mainAgentTimer.resume()
  }

  const isActive = () => timerActive

  return { start, stop, pause, resume, isActive }
}
